import { useNavigate } from 'react-router-dom';
import { Ban, CreditCard, Globe, FileText, LogOut, Settings, HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@artstorefronts/ui';

import { useTranslation } from 'react-i18next';

import { useAuth } from '../context/auth';
import { useSubscription } from '../context/subscription';
import { useAnalytics } from '../context/analytics';
import { UserAvatar } from './UserAvatar';
import { LanguageSelector } from './LanguageSelector';
import { useLocale } from '../hooks/useLocale';
import { useMutation } from '@apollo/client';
import { UPDATE_PROFILE } from '../graphql/mutations';
import { buildTownSquareUrl } from '../utils';

type HideableUserDropdownItem = 'settings' | 'billing' | 'blocked';

interface UserDropdownProps {
  onNavigate?: () => void;
  hideItems?: HideableUserDropdownItem[];
  /** Custom trigger content rendered inside the dropdown button. Defaults to the user avatar. */
  trigger?: ReactNode;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ onNavigate, hideItems = [], trigger }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('nav');
  const { t: tSettings } = useTranslation('settings');
  const { signOut, profile } = useAuth();
  const { hasSubscription, loading: subscriptionLoading } = useSubscription();
  const { intercom } = useAnalytics();
  const { locale, setLocale } = useLocale(profile?.profile?.locale);
  const [updateProfileMutation] = useMutation(UPDATE_PROFILE);

  const handleNavigation = (action: () => void) => {
    action();
    onNavigate?.();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ? (
          <button className="flex items-center justify-center rounded-lg hover:bg-black/5 focus:outline-none p-2">
            {trigger}
          </button>
        ) : (
          <button className="flex items-center rounded-full hover:bg-gray-100 focus:outline-none p-0.5">
            <UserAvatar
              avatarUrl={profile?.profile?.avatarUrl}
              displayName={profile?.profile?.displayName}
              size={32}
              isHumanVerified={!!profile?.profile?.isHumanVerified}
            />
          </button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="z-[200] w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm font-medium text-gray-900 truncate">{profile?.email}</div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {!hideItems.includes('settings') && (
          <DropdownMenuItem onClick={() => handleNavigation(() => navigate('/settings'))}>
            <Settings className="size-4 mr-3" />
            {t('settings')}
          </DropdownMenuItem>
        )}

        {/* The one screen listing every block the viewer holds lives on town-square, so this
            row leaves the SPA entirely: an <a>, never `navigate`. `buildTownSquareUrl` is what
            carries the viewer's `preferred-locale` across as a path prefix — concatenating the
            base URL by hand lands a non-`en` member on the English page.

            Hideable, and hidden by the welcome wizard: `/settings/blocked` is a town-square
            path, and `OnboardingRedirect` deliberately does not consult
            `ONBOARDING_ALLOWED_PATHS` (client routes only), so a locked user would take two
            full page loads back to the wizard — and hand its Done step a continue link the
            client has no route for. Settings and Billing beside it are exempt paths and so
            can stay. */}
        {!hideItems.includes('blocked') && (
          <DropdownMenuItem asChild>
            <a href={buildTownSquareUrl('/settings/blocked')}>
              <Ban className="size-4 mr-3" />
              {tSettings('blocked.title')}
            </a>
          </DropdownMenuItem>
        )}

        {!hideItems.includes('billing') && (
          <DropdownMenuItem
            onClick={() =>
              handleNavigation(() => {
                // `hasSubscription` is false until SUBSCRIPTION_QUERY settles, so acting
                // on it before then sends a subscriber to the upsell. /billing sends them
                // on to /plans itself once it knows there is no subscription.
                if (hasSubscription || subscriptionLoading) {
                  navigate('/billing');
                } else {
                  navigate('/plans');
                }
              })
            }
          >
            <CreditCard className="size-4 mr-3" />
            {t('billing')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={() => window.open('https://www.arthelper.ai/legal/terms-of-service', '_blank')}>
          <FileText className="size-4 mr-3" />
          {t('terms-of-service')}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => {
            intercom.show();
          }}
        >
          <HelpCircle className="size-4 mr-3" />
          {t('support')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <div className="flex items-center px-2 py-1.5">
          <Globe className="size-4 mr-3 text-gray-600 shrink-0" />
          <LanguageSelector
            value={locale}
            onChange={(newLocale) => {
              setLocale(newLocale);
              updateProfileMutation({ variables: { input: { locale: newLocale, localeSource: 'explicit' } } }).catch(
                () => {}
              );
            }}
            variant="minimal"
          />
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-red-600 focus:text-red-600"
          onClick={async () => {
            await signOut();
          }}
        >
          <LogOut className="size-4 mr-3" />
          {t('sign-out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
